import React, { useState, useEffect, useContext } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, DollarSign, AlertCircle, TrendingUp, Users } from 'lucide-react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import TrainingCalendar from '../components/TrainingCalendar';

const HQDashboard = () => {
  const { token } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [franchisees, setFranchisees] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [trainingEvents, setTrainingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, franchiseesRes, kpisRes, trainingRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/franchisees`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/kpi/submissions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/training/events`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setFranchisees(franchiseesRes.data);
      setKpis(kpisRes.data);
      setTrainingEvents(trainingRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Franchisees', value: stats?.total_franchisees || 0, icon: Building2, color: 'bg-primary', testid: 'total-franchisees-stat' },
    { title: 'Active Franchisees', value: stats?.active_franchisees || 0, icon: Users, color: 'bg-chart-4', testid: 'active-franchisees-stat' },
    { title: 'Pending Fees', value: stats?.pending_fees || 0, icon: DollarSign, color: 'bg-secondary', testid: 'pending-fees-stat' },
    { title: 'Open Complaints', value: stats?.open_complaints || 0, icon: AlertCircle, color: 'bg-destructive', testid: 'open-complaints-stat' },
    { title: 'Flagged KPIs', value: stats?.flagged_kpis || 0, icon: TrendingUp, color: 'bg-chart-3', testid: 'flagged-kpis-stat' },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="hq-dashboard">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-primary to-primary-800 rounded-xl p-8 text-white">
          <h1 className="text-3xl font-heading font-bold mb-2">Welcome to Little Caliphs HQ</h1>
          <p className="text-primary-100">Manage and monitor all Islamic kindergarten franchise operations</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title} className="border border-border/60 rounded-xl shadow-sm hover:shadow-md transition-shadow" data-testid={stat.testid}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">{stat.title}</p>
                      <p className="text-3xl font-heading font-bold mt-2 tabular-nums">{stat.value}</p>
                    </div>
                    <div className={`${stat.color} h-12 w-12 rounded-lg flex items-center justify-center text-white`}>
                      <Icon className="h-6 w-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border border-border/60 rounded-xl shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">KPI Performance Overview</CardTitle>
              <CardDescription>Recent KPI scores across franchisees</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={kpis.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="overall_score" fill="#6B46C1" name="Overall Score" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border border-border/60 rounded-xl shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Franchise Growth</CardTitle>
              <CardDescription>Total vs Active Franchisees</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <span className="text-sm font-medium">Total Franchisees</span>
                  <span className="text-2xl font-heading font-bold tabular-nums">{stats?.total_franchisees || 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-primary-50 rounded-lg">
                  <span className="text-sm font-medium">Active Franchisees</span>
                  <span className="text-2xl font-heading font-bold tabular-nums text-primary">{stats?.active_franchisees || 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                  <span className="text-sm font-medium">Inactive Franchisees</span>
                  <span className="text-2xl font-heading font-bold tabular-nums text-secondary">
                    {(stats?.total_franchisees || 0) - (stats?.active_franchisees || 0)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Training Calendar Section */}
        <Card className="border border-border/60 rounded-xl shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading">Training & Events Calendar</CardTitle>
            <CardDescription>Scheduled training sessions and events for all franchisees</CardDescription>
          </CardHeader>
          <CardContent>
            {trainingEvents.length > 0 ? (
              <TrainingCalendar events={trainingEvents} />
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                No training events scheduled yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Franchisees */}
        <Card className="border border-border/60 rounded-xl shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading">Recent Franchisees</CardTitle>
            <CardDescription>Latest registered franchisees</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {franchisees.slice(0, 5).map((franchisee) => (
                <div key={franchisee.franchisee_id} className="flex items-center justify-between p-4 bg-muted rounded-lg hover:bg-accent transition-colors" data-testid={`franchisee-${franchisee.franchise_id}`}>
                  <div>
                    <p className="font-medium">{franchisee.company_name}</p>
                    <p className="text-sm text-muted-foreground">{franchisee.kindergarten.name} - {franchisee.kindergarten.location}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{franchisee.status}</p>
                    <p className="text-xs text-muted-foreground">ID: {franchisee.franchise_id}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default HQDashboard;